import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Calendar,
  MapPin,
  Users,
  ArrowLeft,
  Ticket as TicketIcon,
  PartyPopper,
  Car,
  Backpack,
  Clock,
  Info,
  ChevronDown,
  Image as ImageIcon,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import ConsentBlock from "@/components/ConsentBlock";
import SEO from "@/components/SEO";
import { SkelLine } from "@/components/Skeleton";

function formatWhen(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "long", month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

const whatToBring = [
  "Your doubles / trade stock in a sleeve or binder",
  "A written or phone list of stickers you're chasing",
  "Cash or a phone-ready payment app for on-the-spot trades",
  "Water bottle — venues can get warm during peak hours",
  "Kids? Bring 'em. This event is family-friendly.",
];

const scheduleSample = [
  { time: "Doors open", detail: "Check in at the front table, get your name badge and a soccer-ball sticker." },
  { time: "First 30 min", detail: "Warm-up trades and order pickups. Say hi to Gil." },
  { time: "Main block", detail: "Open trading — walk the floor, meet other collectors." },
  { time: "Wrap-up", detail: "Announcements, upcoming events, and a quick group photo." },
];

const eventFaqs = [
  { q: "Do I need to buy a ticket?", a: "No. RSVP is free. We just ask you to confirm so we know how much space to prepare." },
  { q: "Can I bring my kids?", a: "Yes. Trading Events are family-friendly and kids of all ages are welcome. Parents please stay with young children." },
  { q: "What if I don't have anything to trade yet?", a: "That's fine — many people come just to buy directly from Gil, pick up an order, or watch and learn. There's no pressure to trade." },
  { q: "Is there parking?", a: "Parking availability depends on the venue. Details will appear on this page as we finalize the venue for each event." },
];

export default function EventDetail() {
  const { id } = useParams();
  const [ev, setEv] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", guests: 1, notes: "" });
  const [consent, setConsent] = useState({ required_agreement: false, marketing_email: false, marketing_sms: false });
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    api.get(`/events/${id}`).then((r) => setEv(r.data)).catch((e) => setErr(formatApiError(e.response?.data?.detail) || e.message));
    api.get("/public/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!consent.required_agreement) {
      toast.error("Please confirm the required consent to RSVP.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/events/${id}/rsvp`, { ...form, guests: Number(form.guests), consent });
      toast.success("RSVP confirmed. See you there!");
      setConfirmed(true);
    } catch (err2) {
      toast.error(formatApiError(err2.response?.data?.detail) || err2.message);
    } finally { setBusy(false); }
  };

  if (err) return <div className="container-gm py-24 text-[var(--ink)]">{err}</div>;
  if (!ev) {
    return (
      <div className="container-gm py-14 md:py-20" data-testid="event-detail-loading">
        <SkelLine w="30%" h={14} />
        <div className="mt-6 space-y-4">
          <SkelLine w="70%" h={44} />
          <SkelLine w="55%" h={20} />
          <SkelLine w="60%" h={20} />
        </div>
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-6">
            <SkelLine w="100%" h={140} />
            <SkelLine w="100%" h={220} />
          </div>
          <div><SkelLine w="100%" h={360} /></div>
        </div>
      </div>
    );
  }

  const capUsed = ev.rsvp_count || 0;
  const capLeft = ev.capacity ? Math.max(0, ev.capacity - capUsed) : null;
  const mapQuery = encodeURIComponent(ev.location || "");
  const mapEmbed = `https://www.google.com/maps?q=${mapQuery}&output=embed`;
  // Past events: prefer server-computed `section` (from `/api/events/{id}`)
  // but fall back to the local start-time so the page still renders
  // correctly if hit before the deploy that added `section`.
  const isPast = ev.section
    ? ev.section === "past"
    : (ev.starts_at && new Date(ev.starts_at) < new Date());

  return (
    <div data-testid="event-detail">
      <SEO
        title={ev.title}
        description={(ev.description || "").slice(0, 160) || `RSVP for ${ev.title} — a Gil Maakaroun trading event.`}
      />

      <section className="hero-black">
        <div className="container-gm py-14 md:py-20">
          <Link to="/events" className="inline-flex items-center gap-2 text-white/70 hover:text-[var(--panini-yellow)] text-sm">
            <ArrowLeft size={14} /> Back to events
          </Link>
          <div className="mt-6 flex items-center gap-3">
            <span className="sticker sticker-red">MATCHDAY</span>
            <span className="sticker sticker-white">FREE RSVP</span>
          </div>
          <h1 className="h-display text-white text-4xl md:text-6xl mt-6 max-w-3xl" data-testid="event-title">
            {ev.title}
          </h1>
          <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3 text-white/85">
            <div className="flex items-center gap-2"><Calendar size={16} className="text-[var(--panini-yellow)]" />{formatWhen(ev.starts_at)}{ev.ends_at ? ` – ${formatWhen(ev.ends_at)}` : ""}</div>
            <div className="flex items-center gap-2"><MapPin size={16} className="text-[var(--panini-yellow)]" />{ev.location}</div>
            {ev.capacity && (
              <div className="flex items-center gap-2"><Users size={16} className="text-[var(--panini-yellow)]" />{capUsed} / {ev.capacity} confirmed{capLeft === 0 ? " · full" : ""}</div>
            )}
          </div>
        </div>
        <div className="field-line" aria-hidden />
      </section>

      <section className="container-gm py-14 md:py-20 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-12">
          {/* About */}
          <div>
            <div className="kicker kicker-red">About this event</div>
            <p className="mt-4 text-[var(--ink)] whitespace-pre-wrap leading-relaxed text-base">{ev.description}</p>
          </div>

          {/* Photos */}
          <div data-testid="event-photos">
            <div className="kicker kicker-red">Photos</div>
            <h3 className="font-display text-2xl mt-2">Scenes from the room.</h3>
            <p className="text-[var(--smoke)] text-sm mt-2 max-w-lg">
              Photos from this event will be added after it takes place. Follow
              along on social for the highlights.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="aspect-square rounded-2xl border-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)] flex items-center justify-center"
                >
                  <ImageIcon size={22} className="text-[var(--ink)]/60" />
                </div>
              ))}
            </div>
          </div>

          {/* Map */}
          <div data-testid="event-map">
            <div className="kicker kicker-red">Location</div>
            <h3 className="font-display text-2xl mt-2">{ev.location}</h3>
            <div className="mt-5 rounded-2xl border-2 border-[var(--ink)] overflow-hidden bg-white">
              <iframe
                title="Event location on Google Maps"
                src={mapEmbed}
                className="w-full h-64 md:h-80 border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>

          {/* Parking */}
          <div className="rounded-2xl border-2 border-[var(--ink)] bg-white p-6" data-testid="event-parking">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl border-2 border-[var(--ink)] bg-[var(--field-green)] text-white flex items-center justify-center shrink-0">
                <Car size={20} />
              </div>
              <div>
                <div className="kicker kicker-green">Parking</div>
                <h3 className="font-display text-xl mt-1">Getting there</h3>
                <p className="text-[var(--smoke)] mt-2 text-sm leading-relaxed">
                  Parking details will be posted here as we finalize the venue.
                  Public transit and rideshare are usually easiest for downtown
                  events. Reply to your RSVP email if you need specifics.
                </p>
              </div>
            </div>
          </div>

          {/* What to bring */}
          <div className="rounded-2xl border-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)] p-6" data-testid="event-what-to-bring">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl border-2 border-[var(--ink)] bg-[var(--panini-yellow)] flex items-center justify-center shrink-0">
                <Backpack size={20} />
              </div>
              <div>
                <div className="kicker kicker-red">What to bring</div>
                <h3 className="font-display text-xl mt-1">A quick checklist</h3>
                <ul className="mt-3 space-y-1.5 text-sm text-[var(--ink)]">
                  {whatToBring.map((b, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 mt-2 rounded-full bg-[var(--panini-red)]" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Schedule */}
          <div className="rounded-2xl border-2 border-[var(--ink)] bg-white p-6" data-testid="event-schedule">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl border-2 border-[var(--ink)] bg-[var(--panini-red)] text-white flex items-center justify-center shrink-0">
                <Clock size={20} />
              </div>
              <div className="w-full">
                <div className="kicker kicker-red">Schedule</div>
                <h3 className="font-display text-xl mt-1">Rough run-of-show</h3>
                <p className="text-[var(--smoke)] text-xs mt-1 italic">Timings vary by venue — exact times will appear on this page as we finalize.</p>
                <ol className="mt-4 space-y-3">
                  {scheduleSample.map((s, i) => (
                    <li key={i} className="flex gap-4">
                      <div className="font-mono text-[var(--panini-red)] text-xs w-24 shrink-0 pt-0.5">{s.time}</div>
                      <div className="text-sm text-[var(--ink)] leading-relaxed">{s.detail}</div>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>

          {/* FAQ */}
          <div data-testid="event-faq">
            <div className="kicker kicker-red">FAQ</div>
            <h3 className="font-display text-2xl mt-2">Questions we hear a lot.</h3>
            <div className="mt-5 border-2 border-[var(--ink)] rounded-2xl overflow-hidden divide-y-2 divide-[var(--ink)] bg-white">
              {eventFaqs.map((f, i) => (
                <details key={i} className="group" data-testid={`event-faq-${i}`}>
                  <summary className="cursor-pointer list-none flex items-center justify-between p-5 hover:bg-[var(--panini-yellow-soft)]">
                    <span className="font-display text-base pr-6">{f.q}</span>
                    <ChevronDown size={18} className="shrink-0 transition-transform group-open:rotate-180" />
                  </summary>
                  <div className="px-5 pb-5 text-[var(--smoke)] leading-relaxed text-sm">{f.a}</div>
                </details>
              ))}
            </div>
          </div>
        </div>

        {/* RSVP panel (form logic identical to prior version) */}
        <aside className="lg:sticky lg:top-24 h-fit" data-testid="rsvp-panel">
          <div className="card-fun bg-[var(--panini-yellow)] p-7">
            <div className="flex items-center gap-2">
              <TicketIcon size={18} />
              <div className="kicker kicker-red">
                {isPast ? "Past event" : "Free RSVP"}
              </div>
            </div>
            <h3 className="font-display text-3xl mt-2">
              {isPast ? "Thanks for coming!" : "Save your spot"}
            </h3>

            {isPast ? (
              // Past event — no more RSVPs. Keep the page but replace
              // the form with a neutral "event has ended" panel so
              // the historical content (photos/description) remains
              // reachable while the CTA cannot be misused.
              <div
                className="mt-6 rounded-2xl bg-[var(--ink)] text-white p-6"
                data-testid="event-past-panel"
              >
                <div className="font-display text-2xl">This event has ended.</div>
                <p className="text-white/80 mt-2 text-sm">
                  You can still see the details and photos below.
                </p>
                <Link
                  to="/events"
                  data-testid="event-past-back"
                  className="mt-5 btn-red w-full justify-center inline-flex"
                >
                  See upcoming events →
                </Link>
              </div>
            ) : confirmed ? (
              <div className="mt-6 rounded-2xl bg-[var(--ink)] text-white p-6" data-testid="rsvp-confirmed">
                <PartyPopper size={22} className="text-[var(--panini-yellow)]" />
                <div className="font-display text-2xl mt-3">You&apos;re on the list!</div>
                <p className="text-white/80 mt-2 text-sm">We&apos;ll email a reminder ahead of the event.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-5 space-y-4" data-testid="rsvp-form">
                <input
                  required placeholder="Full name" data-testid="rsvp-name"
                  value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  className="gm-field"
                />
                <input
                  required type="email" placeholder="Email" data-testid="rsvp-email"
                  value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="gm-field"
                />
                <input
                  placeholder="Phone (optional)" data-testid="rsvp-phone"
                  value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="gm-field"
                />
                <div>
                  <div className="kicker kicker-black mb-2 text-[11px]">Guests</div>
                  <input
                    type="number" min={1} max={10} data-testid="rsvp-guests"
                    value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })}
                    className="gm-field w-28"
                  />
                </div>
                <textarea
                  rows={3} placeholder="Notes (optional)" data-testid="rsvp-notes"
                  value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="gm-field"
                />
                <ConsentBlock value={consent} onChange={setConsent} settings={settings} prefix="rsvp-consent" />
                <button
                  type="submit" disabled={busy || capLeft === 0}
                  data-testid="rsvp-submit"
                  className="btn-red w-full justify-center disabled:opacity-60"
                >
                  {busy ? "Confirming…" : capLeft === 0 ? "Event full" : "Confirm RSVP"}
                </button>
              </form>
            )}

            <div className="mt-5 pt-5 border-t border-[var(--ink)]/20 text-xs text-[var(--ink)]/70 flex items-start gap-2">
              <Info size={14} className="shrink-0 mt-0.5" />
              <span>Free · family-friendly · kids welcome · no purchase required.</span>
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}
